import { MIN_WORK_MINUTES } from './drag';
import { shiftHourFromIso } from './geometry';

/**
 * Ganztägig — a Termin shaped to fill one technician's shift. Pure, no React.
 *
 * ```
 * shift 07:00 ──────────────────────────────── 16:00
 *       │travel 30│ work 480              │dep 30│
 *       ▲                                        ▲
 *    block start = shiftStart          block end = shiftEnd
 * ```
 *
 * The **occupied** block equals the shift, so Anfahrt and Abfahrt keep whatever the dispatcher
 * entered and the work segment absorbs the remainder. `plannedStart` is therefore the shift start
 * plus the travel that precedes it — this file speaks the same `plannedStart` as the form and
 * `ScheduleSlot`: the **work** start, never the block start.
 *
 * The Pause is deliberately NOT subtracted. It costs nothing server-side — an all-day block skips
 * the projected-break conflict check (`ScheduleConflictChecker.ignoreBreaks`) — so the block simply
 * covers the Pause on screen. `DispositionBoardWidget` mirrors that skip for its own advisory.
 */

export interface FullDayInput {
  /** `YYYY-MM-DD`, the local calendar day the Termin sits on. */
  dayIso: string;
  /**
   * Profile boundaries. Stored as a wall-clock time-of-day inside a datetime with a dummy date
   * part, hence `shiftHourFromIso` rather than any local-time reader. `null` when unconfigured.
   */
  shiftStart: string | null;
  shiftEnd: string | null;
  travelMinutes: number;
  departureMinutes: number;
}

export interface FullDayResult {
  /** `<input type="datetime-local">` value — local wall clock, `YYYY-MM-DDTHH:mm`. */
  plannedStart: string;
  plannedWorkDuration: number;
}

/**
 * `null` means "there is nothing to derive" — no shift on the profile, boundaries that do not
 * parse, or a non-positive shift. The caller leaves the form exactly as the user left it and says
 * why; silently writing a zero-length Termin would be worse than doing nothing.
 *
 * Deliberately not `shiftWindow()`: that clamps to the drawn 06:00–22:00 board window, which is
 * geometry. A shift is a contract with the technician and keeps its own length here.
 */
export function fullDaySlot(input: FullDayInput): FullDayResult | null {
  const { dayIso, shiftStart, shiftEnd, travelMinutes, departureMinutes } = input;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) return null;
  if (!shiftStart || !shiftEnd) return null;
  if (!Number.isFinite(travelMinutes) || !Number.isFinite(departureMinutes)) return null;

  const startHour = shiftHourFromIso(shiftStart);
  const endHour = shiftHourFromIso(shiftEnd);
  if (Number.isNaN(startHour) || Number.isNaN(endHour)) return null;

  const shiftMinutes = Math.round((endHour - startHour) * 60);
  if (shiftMinutes <= 0) return null;

  // Floored rather than rejected: an Anfahrt plus Abfahrt longer than the shift is a real thing a
  // dispatcher can type, and `plannedWorkDuration` must stay positive for the form schema. The
  // block then runs past shift end and the board says so in amber, which is the honest answer.
  const workMinutes = Math.max(
    MIN_WORK_MINUTES,
    shiftMinutes - travelMinutes - departureMinutes,
  );

  return {
    plannedStart: `${dayIso}T${formatClock(Math.round(startHour * 60) + travelMinutes)}`,
    plannedWorkDuration: workMinutes,
  };
}

/** Minutes past local midnight → `HH:mm`. */
function formatClock(minutes: number): string {
  const clamped = Math.max(0, minutes);
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
